import React, { useState, useEffect, ReactElement } from 'react';

import dynamic from 'next/dynamic';
const Preloader = dynamic(() => import('sections/Company/Faqs/Preloader/Preloader'));
const Navigation = dynamic(() => import('sections/Company/Faqs/Navigation/Navigation'));
const Questions = dynamic(() => import('sections/Company/Faqs/Questions/Questions'));
const Search = dynamic(() => import('sections/Company/Faqs/Search/Search'));

import { useUtils } from 'utils/useUtils';
import { IQuestion, ICategory } from 'sections/Company/Faqs/types';
import { getFaqData } from 'api/requests';
import RootLayout from 'layouts/RootLayout';
import Breadcrumbs from 'components/Breadcrumbs';

export default function HelpCenterPage() {
  const { getDefaultActiveCategoryItems, getQuestionIdFromUrl, renderQuestions, renderFilteredQuestions } = useUtils();

  const [data, setData] = useState([] as ICategory[]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [defaultActiveCategoryId, setDefaultActiveCategoryId] = useState<number | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [activeCategoryItems, setActiveCategoryItems] = useState<IQuestion[]>([]);
  const [activeQuestionId, setActiveQuestionId] = useState<number | null>(null);
  const [activeParentId, setActiveParentId] = useState<number | null>(null);

  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchOptions, setSearchOptions] = useState<any>({
    title: false,
    contents: false,
  });

  const [filteredItems, setFilteredItems] = useState<IQuestion[]>([]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const response = await getFaqData();
      if (response.status === 200) {
        const questionIdFromUrl = getQuestionIdFromUrl();
        setData(response.data.data);
        setIsLoading(false);

        const activeCatId = questionIdFromUrl?.length
          ? questionIdFromUrl[questionIdFromUrl.length - 2]
          : response.data.data[0].id;

        setDefaultActiveCategoryId(activeCatId);
        setActiveCategoryId(activeCatId);

        !questionIdFromUrl && setActiveCategoryItems(getDefaultActiveCategoryItems(activeCatId, response.data.data));
      }
    })();
    const questionIdFromUrl = getQuestionIdFromUrl();
    if (questionIdFromUrl?.length) {
      setActiveQuestionId(questionIdFromUrl[questionIdFromUrl.length - 1]);
      setActiveCategoryId(questionIdFromUrl[questionIdFromUrl.length - 2]);
    }
  }, []);

  useEffect(() => {
    let parentId = null;
    data.forEach((category: ICategory) => {
      if (category.children) {
        category.children.forEach((child: ICategory) => {
          if (child.help_items) {
            child.help_items.forEach((question: IQuestion) => {
              if (question.id === activeQuestionId) {
                parentId = category.id;
              }
            });
          }
        });
      }
    });
    setActiveParentId(parentId);
    setActiveCategoryItems(renderQuestions(data, activeCategoryId));
  }, [data, activeQuestionId]);

  useEffect(() => {
    if (!data) return;
    const result = [...renderFilteredQuestions(data, searchOptions, searchedValue)];
    setFilteredItems(result);
    setActiveCategoryItems(result);

    if (searchedValue === '') {
      setIsSearching(false);
      setActiveCategoryItems(getDefaultActiveCategoryItems(defaultActiveCategoryId, data));
    }

    const urlId = getQuestionIdFromUrl();

    if (searchedValue === '' && urlId) {
      setIsSearching(false);
      setActiveCategoryItems(renderQuestions(data, activeCategoryId));
    }
  }, [data, isSearching, searchOptions, isSearching ? searchedValue : null]);

  useEffect(() => {
    if (searchedValue === '') {
      setIsSearching(false);
      setActiveCategoryItems(getDefaultActiveCategoryItems(defaultActiveCategoryId, data));
    }
  }, [searchedValue]);

  return (
    <section className="faq">
      <div className="container">
        <Breadcrumbs mTop={64} mode="dark" links={[{ title: 'FAQs', href: '' }]} />

        <div className="dfa-app">
          <div className="dfa-app__header">
            <h1 className="dfa-app__heading">FAQs</h1>
            <Search
              setIsSearching={setIsSearching}
              setSearchedValue={setSearchedValue}
              setSearchOptions={setSearchOptions}
            />
          </div>
          {isLoading ? (
            <Preloader />
          ) : (
            <div className="dfa-app__content">
              {isSearching ? (
                filteredItems.length === 0 ? (
                  <p>No results found ...</p>
                ) : (
                  <Questions
                    activeQuestionId={activeQuestionId}
                    filteredItems={filteredItems}
                    //@ts-ignore
                    setActiveQuestionId={filteredItems.length ? 0 : setActiveQuestionId}
                    activeCategoryItems={activeCategoryItems}
                    questionsStyles={{ width: '100%' }}
                  />
                )
              ) : (
                <>
                  <Navigation
                    categories={data}
                    activeCategoryId={activeCategoryId}
                    setActiveCategoryId={setActiveCategoryId}
                    setActiveCategoryItems={setActiveCategoryItems}
                    activeQuestionId={activeQuestionId}
                    activeParentId={activeParentId}
                  />
                  <Questions
                    activeQuestionId={activeQuestionId}
                    setActiveQuestionId={setActiveQuestionId}
                    activeCategoryItems={activeCategoryItems}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export const getStaticProps = async () => {
  return {
    props: {
      title: 'Visit Our FAQ for Assistance | DF Alliance',
      description:
        'Visit the DF Alliance FAQ for a comprehensive range of resources, FAQs, and support to guide you through the freight forwarding process.',
      path: '/company/faqs',
    },
  };
};

HelpCenterPage.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
